import {useCallback, useEffect, useRef, useState} from 'react';
import {useFeatureValue} from '@growthbook/growthbook-react';
import {useValueParams} from '../utils/dynamicCodes/hooks';
import {growthBook} from '../index';
import Analytics from '../utils/Analytics';
import {PAYWALL_EVENTS, PaywallSlug} from '../analytics/paywallEvents';

// I21 — reusable "explicit dismiss mechanism" treatment. Any paywall that renders the
// second-chance offer sheet (via `PaymentIntentScreen`'s `secondChanceOffer` +
// `secondChanceOpenerRef`) can layer one of these dismiss triggers on top with a few
// lines — no per-experiment screen duplication:
//
//   const {variant, revealMs, idleMs} = useDismissTreatmentConfig();
//   const openerRef = useRef<(source?: string) => void>();
//   const {showCloseButton, closeButtonVisible, triggerDismiss} = useDismissTreatment({
//     variant, revealMs, idleMs,
//     ready: paymentReady,
//     openOffer: source => openerRef.current?.(source),
//   });
//   // …render <DismissCloseButton visible={closeButtonVisible} onClick={triggerDismiss} />
//   // …pass secondChanceOpenerRef={openerRef} to <PaymentIntentScreen />
//
// The three treatments:
//   • immediate-x — a top-right ✕ visible as soon as the paywall is ready
//   • delayed-x   — the same ✕, hidden on load, fades in after revealMs of dwell
//   • idle-time   — no affordance; the offer auto-opens after idleMs of inactivity,
//                   and any interaction resets the timer

export type DismissVariant = 'immediate-x' | 'delayed-x' | 'idle-time';

const VALID_VARIANTS: DismissVariant[] = ['immediate-x', 'delayed-x', 'idle-time'];

export const REVEAL_MS = 10000; // delayed-x default: dwell before the ✕ fades in (≈ median buyer CTA tap, 10.6s)
export const IDLE_MS = 8000; // idle-time default: inactivity before the offer auto-opens

// GrowthBook feature (JSON) that configures the treatment. Shape:
//   { "variant": "immediate-x" | "delayed-x" | "idle-time" | "off", "revealMs"?: number, "idleMs"?: number }
// Default is `{ variant: "off" }` so the treatment is inert unless the flag turns it on.
// A GrowthBook *experiment* on this feature serves a different `variant` per arm.
export const DISMISS_TREATMENT_FLAG = 'web_payments_dismiss_treatment';

export type DismissTreatmentConfig = {
  variant: DismissVariant | 'off';
  revealMs?: number;
  idleMs?: number;
};

type ResolvedDismissConfig = {
  variant: DismissVariant | 'off';
  revealMs: number;
  idleMs: number;
  /** GrowthBook has loaded its flags (or a QA override decides the arm), so `variant` is
   * real rather than the fallback. App mounts the routes after a 1.5 s timeout even if
   * GrowthBook is still loading. */
  configReady: boolean;
};

const DEFAULT_CONFIG: DismissTreatmentConfig = {variant: 'off'};

// Analytics `source` tag threaded through to PaymentIntentScreen's dismiss-offer events.
export const SOURCE_BY_VARIANT: Record<DismissVariant, string> = {
  'immediate-x': 'corner_x_immediate',
  'delayed-x': 'corner_x_delayed',
  'idle-time': 'idle_timeout',
};

const isVariant = (v: unknown): v is DismissVariant =>
  typeof v === 'string' && (VALID_VARIANTS as string[]).includes(v);

// Coerce an untrusted GrowthBook JSON value (+ optional URL override) into a safe config.
const resolveConfig = (raw: unknown, urlOverride?: string | null): Omit<ResolvedDismissConfig, 'configReady'> => {
  const cfg = (raw && typeof raw === 'object' ? raw : {}) as Partial<DismissTreatmentConfig>;
  let variant: DismissVariant | 'off' = isVariant(cfg.variant) ? cfg.variant : 'off';
  // QA override: `?dismissTreatment=` forces a variant (or `off`) regardless of GrowthBook
  // assignment, so a treatment can be exercised on a device without enrolling.
  if (urlOverride === 'off') {
    variant = 'off';
  } else if (isVariant(urlOverride)) {
    variant = urlOverride;
  }
  const revealMs = typeof cfg.revealMs === 'number' && cfg.revealMs >= 0 ? cfg.revealMs : REVEAL_MS;
  const idleMs = typeof cfg.idleMs === 'number' && cfg.idleMs > 0 ? cfg.idleMs : IDLE_MS;
  return {variant, revealMs, idleMs};
};

// Reads the dismiss-treatment config from GrowthBook, with the `dismissTreatment` URL
// param as a QA override. Returns `variant: 'off'` (inert) by default, so a paywall that
// adopts the hook does nothing until the flag/experiment turns it on.
export const useDismissTreatmentConfig = (): ResolvedDismissConfig => {
  const urlOverride = useValueParams('dismissTreatment');
  // Reactive read: re-renders when GrowthBook's flags or attributes (personId) change, so a
  // late GrowthBook load still reaches the screen.
  const gbValue = useFeatureValue<DismissTreatmentConfig>(DISMISS_TREATMENT_FLAG, DEFAULT_CONFIG);
  const configReady = Boolean(growthBook.ready) || urlOverride != null;
  return {...resolveConfig(gbValue, urlOverride), configReady};
};

type UseDismissTreatmentParams = {
  /** Active treatment, or 'off' to disable (nothing renders, no timers arm). */
  variant: DismissVariant | 'off';
  /** Payment UI is ready — gates both the affordance and the idle timer. */
  ready: boolean;
  /** Paywall slug for the Dismiss Treatment Assigned event. */
  paywall?: PaywallSlug;
  /** From useDismissTreatmentConfig: the arm is final. The assigned event waits for it. */
  configReady?: boolean;
  /** delayed-x dwell before the ✕ fades in (ms). Defaults to REVEAL_MS. */
  revealMs?: number;
  /** idle-time inactivity before the offer auto-opens (ms). Defaults to IDLE_MS. */
  idleMs?: number;
  /**
   * Opens the second-chance sheet. Called on every ✕ tap / idle fire — the affordance is
   * persistent and re-openable, so this may fire more than once per session.
   */
  openOffer: (source?: string) => void;
};

type UseDismissTreatmentResult = {
  /** Whether to render the corner ✕ (immediate-x / delayed-x). Persistent once shown. */
  showCloseButton: boolean;
  /** ✕ opacity gate — false during the delayed-x fade-in window, then stays true. */
  closeButtonVisible: boolean;
  /** ✕ onClick handler (also the manual entry point for any custom affordance). */
  triggerDismiss: () => void;
  /**
   * Call when the user starts checking out (taps the CTA, which opens the native Apple/
   * Google Pay sheet, or picks the credit card form). Disarms the idle-time timer for good:
   * the page sees no taps while the native sheet is open (median ~15s) and none from inside
   * the Stripe card iframe, so the timer would otherwise fire on someone mid-purchase.
   * Pass it to PaymentIntentScreen's `onCheckoutStarted`.
   */
  onCheckoutStarted: () => void;
};

export const useDismissTreatment = ({
  variant,
  ready,
  paywall,
  configReady = true,
  revealMs = REVEAL_MS,
  idleMs = IDLE_MS,
  openOffer,
}: UseDismissTreatmentParams): UseDismissTreatmentResult => {
  // Tracks whether the idle-time timer has already fired once (it auto-opens a single
  // time per session). The ✕ affordance is NOT gated on this — it stays put and can be
  // tapped again to reopen the offer.
  const [idleFired, setIdleFired] = useState(false);
  // Set once the user starts checking out; the idle timer never re-arms after that.
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  // delayed-x reveal state only; immediate-x visibility is derived from the current variant
  // below, so an arm that resolves after the first render (personId is set during Stripe
  // setup) still shows the ✕.
  const [delayedRevealed, setDelayedRevealed] = useState(false);

  // Keep the latest openOffer without re-arming timers on every render.
  const openOfferRef = useRef(openOffer);
  openOfferRef.current = openOffer;

  const triggerDismiss = () => {
    if (variant === 'off') return;
    openOfferRef.current(SOURCE_BY_VARIANT[variant]);
  };

  // Stable handle for the idle timer so it doesn't tear down/re-arm each render.
  const triggerRef = useRef(triggerDismiss);
  triggerRef.current = triggerDismiss;

  // Record the arm once per page view, when the payment UI is ready and GrowthBook has
  // loaded. Before then the variant can still read 'off' (GrowthBook gets the user's
  // personId during Stripe setup, and may itself still be loading), which is why the arm
  // isn't a property of Paywall Viewed.
  const assignedTrackedRef = useRef(false);
  useEffect(() => {
    if (!ready || !configReady || assignedTrackedRef.current) return;
    assignedTrackedRef.current = true;
    Analytics.track(PAYWALL_EVENTS.dismissTreatmentAssigned, {
      paywall,
      dismissTreatment: variant,
      ...(variant === 'delayed-x' ? {revealMs} : {}),
      ...(variant === 'idle-time' ? {idleMs} : {}),
    });
  }, [ready, configReady, variant, paywall, revealMs, idleMs]);

  // delayed-x: reveal the ✕ after a dwell so fast/committed buyers never see it. Once
  // revealed it stays (persistent).
  useEffect(() => {
    if (variant !== 'delayed-x' || !ready) return;
    const id = window.setTimeout(() => setDelayedRevealed(true), revealMs);
    return () => window.clearTimeout(id);
  }, [variant, ready, revealMs]);

  // idle-time: auto-open the offer after IDLE_MS of no interaction; any interaction resets
  // the timer. Fires once per session (no affordance to reopen it), and never once the user
  // has started checking out (see onCheckoutStarted).
  useEffect(() => {
    if (variant !== 'idle-time' || !ready || idleFired || checkoutStarted) return;
    const fire = () => {
      setIdleFired(true);
      triggerRef.current();
    };
    let id = window.setTimeout(fire, idleMs);
    const reset = () => {
      window.clearTimeout(id);
      id = window.setTimeout(fire, idleMs);
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    events.forEach(e => window.addEventListener(e, reset, {passive: true}));
    return () => {
      window.clearTimeout(id);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [variant, ready, idleFired, checkoutStarted, idleMs]);

  // Persistent: the ✕ stays visible even after the offer has been opened/attempted.
  const showCloseButton = (variant === 'immediate-x' || variant === 'delayed-x') && ready;

  const onCheckoutStarted = useCallback(() => setCheckoutStarted(true), []);

  const closeButtonVisible = variant === 'immediate-x' || (variant === 'delayed-x' && delayedRevealed);

  return {showCloseButton, closeButtonVisible, triggerDismiss, onCheckoutStarted};
};
